import React, { useEffect, useMemo } from 'react';
import { Layout, Button, Badge, Dropdown, Avatar, Space, Typography, Tag } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  BellOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { COLORS } from '../../constants/colors';
import { SPACING, SIZE, FONT_SIZE } from '../../constants/design';
import { useNotificationStore } from '../../stores/notificationStore';
import { ROUTES } from '../../config/routes';
import { NotificationPopover } from '../notification/NotificationPopover';

const { Header: AntHeader } = Layout;
const { Text } = Typography;

interface HeaderProps {
  collapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
}

export const Header: React.FC<HeaderProps> = ({ collapsed, onCollapse }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { unreadCount, fetchUnreadCount } = useNotificationStore();

  // 每30秒轮询刷新未读数
  useEffect(() => {
    // 初始获取一次
    fetchUnreadCount();

    // 设置轮询
    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 30000);

    // 组件卸载时清除定时器
    return () => {
      clearInterval(interval);
    };
  }, [fetchUnreadCount]);

  // 角色显示名称映射
  const roleLabels: Record<string, string> = {
    owner: '所有者',
    admin: '管理员',
    operator: '操作员',
    viewer: '查看者',
  };

  const handleLogout = async () => {
    await logout();
    navigate(ROUTES.LOGIN);
  };

  // 根据用户角色生成菜单项
  const userMenuItems = useMemo(() => {
    const items: any[] = [
      {
        key: 'profile',
        icon: <UserOutlined />,
        label: '个人设置',
        onClick: () => navigate(ROUTES.PROFILE),
      },
    ];

    // 只有 owner 和 admin 才能看到系统设置
    if (user?.role === 'owner' || user?.role === 'admin') {
      items.push({
        key: 'settings',
        icon: <SettingOutlined />,
        label: '系统设置',
        onClick: () => navigate(ROUTES.SETTINGS),
      });
    }

    items.push(
      {
        type: 'divider' as const,
      },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: '退出登录',
        onClick: handleLogout,
      }
    );

    return items;
  }, [user?.role, navigate]);

  return (
    <AntHeader
      style={{
        padding: `0 ${SPACING.xl}px`,
        background: 'var(--color-white)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: 'var(--shadow-md)',
      }}
    >
      <Space size={SPACING.base}>
        <Button
          type="text"
          icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={() => onCollapse(!collapsed)}
          style={{ fontSize: FONT_SIZE.md, width: SIZE.headerHeight, height: SIZE.headerHeight }}
        />
        <Text strong style={{ fontSize: FONT_SIZE.md, color: 'var(--color-text-dark)' }}>
          NexusInflu
        </Text>
      </Space>

      <Space size={SPACING.xl}>
        {/* 通知铃铛 */}
        <NotificationPopover>
          <Badge count={unreadCount} size="small">
            <Button
              type="text"
              icon={<BellOutlined style={{ fontSize: '18px' }} />}
            />
          </Badge>
        </NotificationPopover>

        {/* 用户头像下拉菜单 */}
        <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
          <Space style={{ cursor: 'pointer' }}>
            <Avatar
              src={user?.avatar}
              icon={!user?.avatar && <UserOutlined />}
              style={{ backgroundColor: user?.avatar ? undefined : COLORS.primary }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Text strong style={{ lineHeight: 1.2 }}>{user?.name || '用户'}</Text>
              <Tag color="blue" style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px', margin: 0 }}>
                {roleLabels[user?.role || 'viewer']}
              </Tag>
            </div>
          </Space>
        </Dropdown>
      </Space>
    </AntHeader>
  );
};
